import { CreditCard, MessageSquare, Settings, Plus, Moon, Sun, Package } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { preserveQueryParams, navigateWithParams } from '@/lib/navigation-utils';
import { useTheme } from '@/hooks/useTheme';
import { LEGACY_FEATURES_ENABLED } from '@/lib/features';

// Legacy navigation (chat, history, billing) — only when VITE_ENABLE_LEGACY_FEATURES=true.
const legacyMenuItems = [
  {
    title: 'New Chat',
    icon: Plus,
    url: '/',
    newChat: true,
  },
  {
    title: 'Chats',
    icon: MessageSquare,
    url: '/chats',
  },
  {
    title: 'Add Hardware',
    icon: Package,
    url: '/hardware',
  },
  {
    title: 'Billing',
    icon: CreditCard,
    url: '/billing',
  },
  {
    title: 'Settings',
    icon: Settings,
    url: '/settings',
  },
];

const productionMenuItems = [
  {
    title: 'Add Hardware',
    icon: Package,
    url: '/',
  },
  {
    title: 'Settings',
    icon: Settings,
    url: '/settings',
  },
];

const menuItems = LEGACY_FEATURES_ENABLED ? legacyMenuItems : productionMenuItems;

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile, setOpenMobile } = useSidebar();
  const { theme, toggleTheme } = useTheme();

  const handleNewChatClick = () => {
    if (isMobile) setOpenMobile(false);
    // Use programmatic navigation with state to force React Router to treat it as new navigation
    // even when already on the same route
    navigateWithParams(navigate, '/', {
      replace: false,
      state: { resetChat: Date.now() }
    });
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="h-12 border-b border-sidebar-border justify-center">
        <div className="flex items-center gap-2 px-2">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <span className="text-xs font-semibold">F</span>
          </div>
          <div className="flex flex-col group-data-[collapsible=icon]:hidden">
            <span className="text-xs font-semibold text-sidebar-foreground">FastenAI</span>
            <span className="text-[10px] text-sidebar-foreground/70">Otto</span>
          </div>
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => {
                const isActive = 
                  (item.url === '/' && location.pathname === '/') ||
                  (item.url === '/chats' && location.pathname.startsWith('/chats') && location.pathname !== '/chats') ||
                  (item.url !== '/' && item.url !== '/chats' && location.pathname === item.url);
                
                // Special handling for "New Chat" button - use programmatic navigation
                if (item.newChat) {
                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        isActive={isActive}
                        onClick={handleNewChatClick}
                      >
                        <item.icon />
                        <span>{item.title}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                }
                
                // Other menu items use Link as before
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive}
                      onClick={() => {
                        if (isMobile) setOpenMobile(false);
                      }}
                    >
                      <Link to={preserveQueryParams(item.url)}>
                        <item.icon />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={toggleTheme}
              tooltip={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            >
              {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
              <span>{theme === 'light' ? 'Dark mode' : 'Light mode'}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}

